import {
  IGetPaginatedAttributeProps,
  IGetPaginatedByTextAttributeProps,
  IPublicGetPaginatedAttributeProps
} from "./interfaces";
import { useEffect, useState } from "react";
import { StatusCodesHelper } from "../../models/StatusCodesHelper";
import { toast } from "react-toastify";
import { DataStorage } from "../../models/DataStorage";

export const useGetPaginatedAttributes = <T>(
  pageSize: number,
  backendRequest: (
    parameters:
      | IGetPaginatedAttributeProps
      | IPublicGetPaginatedAttributeProps
      | IGetPaginatedByTextAttributeProps
  ) => Promise<{ body: any; status: number }>,
  setLoading: (loading: boolean) => void,
  storage: DataStorage<T>,
  isPublic: boolean,
  isEnabled: boolean,
  collectionId?: string,
  query?: string
) => {
  const [items, setItems] = useState<T[]>([]);
  const [lastUuid, setLastUuid] = useState<string>();
  const [noMoreItems, setNoMoreItems] = useState(false);
  const [forceFetch, setForceFetch] = useState(false);
  useEffect(() => {
    if (noMoreItems || !isEnabled) return;
    const currentData = storage.getData(isPublic, collectionId);
    if ((!noMoreItems && lastUuid) || !currentData || currentData.length == 0) {
      fetchData();
    } else {
      setItems(currentData);
      setNoMoreItems(true);
      setLoading(false);
    }
  }, [lastUuid, forceFetch]);
  useEffect(() => {
    if (noMoreItems) {
      storage.setData(items, isPublic, collectionId);
    }
  }, [items]);
  const fetchData = () => {
    setLoading(true);
    backendRequest({ pageSize, lastUuid, collectionId, query }).then(({ body, status }) => {
      if (StatusCodesHelper.isSuccessful(status)) {
        const isLastPage = body.length < pageSize;
        if (isLastPage) setLoading(false);
        setNoMoreItems(isLastPage);
        if (body.length >= 0) {
          setItems(oldArray => [...oldArray, ...body]);
          if (body.length > 0) {
            setLastUuid(body.slice(-1)[0].uuid);
          }
        }
      } else if (!StatusCodesHelper.isSessionExpired(status)) {
        setNoMoreItems(true);
        toast.error(body.message);
        setLoading(false);
      }
    });
  };
  const resetItems = () => {
    setItems([]);
    storage.setData(undefined, isPublic, collectionId);
    setNoMoreItems(false);
    if (lastUuid === undefined) {
      setForceFetch(state => !state);
    } else {
      setLastUuid(undefined);
    }
  };
  return [items, resetItems, setItems, noMoreItems] as const;
};
